import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

interface NavBarProps {
  userName: string;
  userEmail: string;
  /** highlight active tab: "home" | "profile" */
  active?: 'home' | 'profile';
  onLogout?: () => void;
}

/**
 * Persistent top navigation bar shown on authenticated screens.
 * Glass-morphism styling consistent with the rest of the design system.
 */
export default function NavBar({ userName, userEmail, active, onLogout }: NavBarProps) {
  const navigate = useNavigate();

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <header style={navStyle}>
      {/* Left: Logo */}
      <button
        onClick={() => navigate('/app')}
        style={logoButtonStyle}
        aria-label="Go to home"
      >
        <div style={logoMarkStyle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
              stroke="url(#logo-grad)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <defs>
              <linearGradient id="logo-grad" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <span style={logoTextStyle}>SocialLogin</span>
      </button>

      {/* Center: Nav links */}
      <nav style={navLinksStyle}>
        <button
          onClick={() => navigate('/app')}
          style={navLinkStyle(active === 'home')}
        >
          Home
        </button>
        <button
          onClick={() => navigate('/claims')}
          style={navLinkStyle(active === 'profile')}
        >
          Profile
        </button>
      </nav>

      {/* Right: User identity + logout */}
      <div style={rightSectionStyle}>
        {/* Avatar */}
        <button
          onClick={() => navigate('/claims')}
          style={userBadgeStyle}
          title={`${userName} · ${userEmail}`}
          aria-label={`${userName} account`}
        >
          <div style={avatarStyle}>{initials}</div>
        </button>

        {/* Logout */}
        <button
          style={logoutBtnStyle}
          onClick={onLogout}
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

/* ---- Styles ---- */

const navStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 28px',
  height: '60px',
  backgroundColor: 'rgba(7, 2, 26, 0.7)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
};

const logoButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'none',
};

const logoMarkStyle: CSSProperties = {
  width: '34px',
  height: '34px',
  borderRadius: '10px',
  background: 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(59,130,246,0.25))',
  border: '1px solid rgba(255,255,255,0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const logoTextStyle: CSSProperties = {
  fontSize: '15px',
  fontWeight: 700,
  color: '#ffffff',
  letterSpacing: '-0.02em',
};

const navLinksStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
};

const navLinkStyle = (isActive: boolean): CSSProperties => ({
  background: isActive ? 'rgba(168,85,247,0.15)' : 'none',
  border: '1px solid ' + (isActive ? 'rgba(168,85,247,0.3)' : 'transparent'),
  borderRadius: '8px',
  padding: '6px 16px',
  fontSize: '14px',
  fontWeight: isActive ? 600 : 400,
  color: isActive ? '#d8b4fe' : 'rgba(255,255,255,0.55)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
});

const rightSectionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const userBadgeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '20px',
  padding: '4px 12px 4px 4px',
  cursor: 'pointer',
};

const avatarStyle: CSSProperties = {
  width: '26px',
  height: '26px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #a855f7, #3b82f6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 700,
  color: '#ffffff',
  flexShrink: 0,
};

const logoutBtnStyle: CSSProperties = {
  background: 'rgba(239,68,68,0.1)',
  border: '1px solid rgba(239,68,68,0.25)',
  borderRadius: '8px',
  padding: '6px 14px',
  fontSize: '13px',
  fontWeight: 500,
  color: 'rgba(252,165,165,0.9)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};
